
import {
  defineComponent, onBeforeMount, PropType, Ref, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { FieldConfig, ParserFieldObject, Property, CUSTOMIZABLE_ICON_STYLES_V4 } from '@/types';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';
import Color from '@/components/template-builder/fields/Color.vue';
import Dropdown from 'primevue/dropdown';
// eslint-disable-next-line import/no-cycle
import { setSocialNetworkIconColorV4 } from '@/components/template-builder/setters';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

export default defineComponent({
  name: 'SocialIconsStyleV4',

  components: {
    ToggleDisplaySettings,
    Dropdown,
    Color,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['on-change-properties'],

  setup(props, context) {
    const { t } = useI18n();
    const configs = ref(props.configs);
    // Si displayToggle est false, toujours afficher les propriétés
    const displayProperties = ref(configs.value.displayToggle === false ? true : !(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));
    const property: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties[0]);
    const selectedValue = ref(property.value.value ?? props.configs.options.defaultValue);
    const options = ref([]);

    // Extract current color from existing colorized icon URLs (synchronous)
    const getInitialIconColor = (): string => {
      const template = getTemplateIframeDocument();
      if (!template) return '#000000';

      // Le sélecteur pointe déjà vers les images (ex: ... > table img)
      const images: NodeListOf<HTMLImageElement> = template.querySelectorAll(props.parserValues.selector);
      const patternColorized = /^https:\/\/media\.shopimind\.(?:com|io)\/clri\/i-[0-9]{1,2}\/([a-fA-F0-9]{6})\/.+\.png$/i;

      for (let i = 0; i < images.length; i++) {
        const src = images[i].getAttribute('src');
        if (!src) continue;
        const match = src.match(patternColorized);
        if (match) {
          return `#${match[1]}`;
        }
      }
      return '#000000';
    };

    const iconColor = ref(getInitialIconColor());
    let colorChangeTimeout: ReturnType<typeof setTimeout> | null = null;

    // Vérifie si le style sélectionné est personnalisable
    const isCustomizableStyle = computed(() => {
      return CUSTOMIZABLE_ICON_STYLES_V4.includes(selectedValue.value);
    });

    // Vérifie si un style donné est personnalisable (pour le template)
    const isStyleCustomizable = (styleValue: string) => {
      return CUSTOMIZABLE_ICON_STYLES_V4.includes(styleValue);
    };

    // Configuration pour le composant Color
    const colorFieldConfig = computed(() => ({
      label: 'templateBuilder.fields.iconColor',
      displayToggle: false,
      options: {
        defaultValue: iconColor.value,
        showAlpha: false,
        showGradient: false,
      },
      properties: [{
        name: 'iconColor',
        value: iconColor.value,
        getter: () => iconColor.value,
        setters: [],
      }],
    }));

    const colorParserValues = computed(() => ({
      selector: props.parserValues.selector,
      properties: [{
        name: 'iconColor',
        value: iconColor.value,
        getter: () => iconColor.value,
        setters: [],
      }],
    }));

    const handleSelected = () => {
      if (property.value) {
        property.value.value = selectedValue.value;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };

    const handleColorChange = (event: any) => {
      if (event.properties && event.properties[0]) {
        const newColor = event.properties[0].value as string;

        // Only accept 6-character hex colors (e.g., #FF0000)
        const hex6Regex = /^#[A-Fa-f0-9]{6}$/;
        if (!hex6Regex.test(newColor)) {
          return;
        }

        iconColor.value = newColor;
      }
      // Debounce: annuler le timeout précédent et en créer un nouveau
      if (colorChangeTimeout) {
        clearTimeout(colorChangeTimeout);
      }
      colorChangeTimeout = setTimeout(() => {
        // Appliquer la colorisation directement via le setter
        // Ne pas émettre on-change-properties car cela déclencherait le setter de style qui écraserait les URLs colorisées
        setSocialNetworkIconColorV4(props.parserValues.selector, iconColor.value, selectedValue.value);
      }, 300);
    };

    onBeforeMount(async () => {
      try {
        options.value = await props.configs.options.list;
      } catch (error) {
        options.value = [];
      }
    });

    return {
      t,
      displayProperties,
      selectedValue,
      options,
      handleSelected,
      isCustomizableStyle,
      isStyleCustomizable,
      colorFieldConfig,
      colorParserValues,
      handleColorChange,
    };
  },

});
